import axios from 'axios';
import dayjs from 'dayjs';
import sydLogo from '../assets/logo.png';
import altiusLogo from '../assets/altius_logo.png';
import sydTapDownloadLogo from '../assets/v2/onboarding/new/syd/tap_to_download_logo.png';
import altiusTapToDownloadLogo from '../assets/v2/onboarding/new/altius/tap_to_download_logo.png';

// 1. Update Theme Interface
export interface Theme {
  colours: {
    primary: string;
    primary08: string;
    primary20: string;
    primary40: string;
    primary60: string;
    primary80: string;

    mint: string;
    mint70: string;
    mint20: string;
    mint40: string;
    yellow: string;

    red: string;
    red20: string;

    white80: string;
    white60: string;
    white40: string;
    white20: string;
    white8: string;

    kpiBg: string;
    greyBg: string;

    purple: string;

    lightMint: string;

    coral: string;
    limeYellow: string;

    appGrey: string;
    onboardingDarkText: string;

    innerButtonText: string;
    activityTypeText: string;
    secondaryGrayText: string;
    tertiaryGrayText: string;
  };
  appName: string;
  showOnboardingAnimation: boolean;
  defaultCountry: string;
  hideNonBinary?: boolean;

  images: {
    logo: string;
    tapToDownloadLogo: string;
  };

  // Method to get the environment-specific QR link
  getQRLink: (email?: string) => string;
}

// 2. Update Syd Theme
export const sydTheme: Theme = {
  colours: {
    primary: '#292D32',
    primary08: '#292D3214',
    primary20: '#292D3233',
    primary40: '#292D3266',
    primary60: '#292D3299',
    primary80: '#292D32CC',

    mint: '#0ABAB5',
    mint70: '#0ABAB5B2',
    mint20: '#0ABAB533',
    mint40: '#0ABAB566',
    yellow: '#F7C824',

    red: '#E84D8A',
    red20: '#E84D8A33',

    white80: '#FFFFFFCC',
    white60: '#FFFFFF99',
    white40: '#FFFFFF66',
    white20: '#FFFFFF33',
    white8: '#FFFFFF14',

    kpiBg: '#EBEDF0',
    greyBg: '#F5F6F6',

    purple: '#8D09F7',

    lightMint: '#51C0CD',

    coral: '#EE7E61',
    limeYellow: '#E3DB28',

    appGrey: '#333333',
    onboardingDarkText: '#FFFFFF66',

    innerButtonText: '#FFFFFF',
    activityTypeText: '#0ABAB5',
    secondaryGrayText: '#757D87',
    tertiaryGrayText: '#757D87CC',
  },
  appName: 'Syd',
  showOnboardingAnimation: true,
  defaultCountry: 'GB',

  images: {
    logo: sydLogo,
    tapToDownloadLogo: sydTapDownloadLogo,
  },

  getQRLink: (email) =>
    `https://yzk5e.app.link/Sozh5Tqwcub?email=${email}&signIn=true`,
};

// 3. Update Altius Theme
export const altiusTheme: Theme = {
  colours: {
    primary: '#040B28',
    primary08: '#040B2814',
    primary20: '#040B2833',
    primary40: '#040B2866',
    primary60: '#040B2899',
    primary80: '#040B28CC',

    // mint: '#96DD3A',
    mint: '#7dc304',
    mint70: '#96DD3AB2',
    mint20: '#96DD3A33',
    mint40: '#96DD3A66',
    yellow: '#FFBC00',

    red: '#77220B',
    red20: '#77220B33',

    white80: '#FFFFFFCC',
    white60: '#FFFFFF99',
    white40: '#FFFFFF66',
    white20: '#FFFFFF33',
    white8: '#FFFFFF14',

    kpiBg: '#EBEDF0',
    greyBg: '#F5F8F8',

    purple: '#020F50',

    lightMint: '#80BD31',

    coral: '#BB6F05',
    limeYellow: '#CACC1D',

    appGrey: '#333333',
    onboardingDarkText: '#D8E2E4',

    innerButtonText: '#020F50',
    activityTypeText: '#020F50',
    secondaryGrayText: '#040B2866',
    tertiaryGrayText: '#040B2844',
  },

  appName: 'Alti',
  showOnboardingAnimation: false,
  defaultCountry: 'US',
  hideNonBinary: true,

  images: {
    logo: altiusLogo,
    tapToDownloadLogo: altiusTapToDownloadLogo,
  },

  getQRLink: () => {
    return 'https://75x7w.app.link/RJx51I1TN0b';
  },
};

interface Environment {
  mlURL: string;
  rubyURL: string;
  nodeURL: string;
  defaultCoupon: string;
  autocomplete: boolean;
  mlAPIKey: string;

  cognitoUserPoolId: string;
  cognitoUserPoolWebClientId: string;
  cognitoRegion: string;

  amplitudeAPIKey: string;
  stripeKey: string;

  timeout: number;
  splashTimeout: number;

  hotjarSideId: number;
  hotjarVersion: number;
  name: string;

  termsURL: string;

  theme: Theme;
}

const alpha: Environment = {
  mlURL: 'https://alpha.ml.iamyiam.com',
  rubyURL: 'https://alpha.app.iamyiam.com/api',
  nodeURL: 'https://alpha.app.iamyiam.com/api/v2',
  defaultCoupon: 'test_website_subscription',
  autocomplete: false,
  mlAPIKey: '12121212',

  cognitoUserPoolId: 'eu-west-2_A24fw6S6u',
  cognitoUserPoolWebClientId: '1f6ar791d6s1dk1mpar4rm1c0b',
  cognitoRegion: 'eu-west-2',

  amplitudeAPIKey: '2325935557c37c6717c3fee5e9ff479e',
  stripeKey: 'pk_test_BNYkNTn9O1FZZj33ETnYSQ4G',

  timeout: 2000,
  splashTimeout: 5000,

  hotjarSideId: 3906039,
  hotjarVersion: 6,
  name: 'alpha',
  termsURL: 'https://syd.life/terms/user_terms.html',

  theme: sydTheme,
};

const prod: Environment = {
  mlURL: 'https://ml.iamyiam.com',
  rubyURL: 'https://www.iamyiam.com/api',
  nodeURL: 'https://www.iamyiam.com/api/v2',
  defaultCoupon: 'website_payment_subscription',
  autocomplete: false,
  mlAPIKey: 'a57858f1-9824-4a02-a2de-b4c756ae4805',

  cognitoUserPoolId: 'eu-west-2_6YqPkSazd',
  cognitoUserPoolWebClientId: '3de8prgg2khpp20b8gco312995',
  cognitoRegion: 'eu-west-2',

  amplitudeAPIKey: 'b204f9666a6102ed176606f2260b908c',
  stripeKey: 'pk_live_a2H2ivpmYZvwZAXv1FngUBL3',

  timeout: 2000,
  splashTimeout: 5000,

  hotjarSideId: 3905205,
  hotjarVersion: 6,
  name: 'production',
  termsURL: 'https://syd.life/terms/user_terms.html',

  theme: sydTheme,
};

const prodNodeLocalhost = {
  ...prod,
  nodeURL: 'http://localhost:8000/api/v2',
};

const alphaNodeLocalhost = {
  ...alpha,
  nodeURL: 'http://localhost:9001/api/v2',
};

const dev = {
  ...alpha,
  autocomplete: true,
  timeout: 200,
  splashTimeout: 1000,
};

const alphaRubyLocalhost: Environment = {
  ...dev,
  rubyURL: 'http://localhost:3000/api',
};

const alphaLocalhost = {
  ...dev,
  nodeURL: 'http://localhost:8000/api/v2',
  rubyURL: 'http://localhost:3000/api',
};

const altius: Environment = {
  ...prod,

  mlURL: 'https://api.ml.altius.syd.life',
  rubyURL: 'https://api.app.altius.syd.life/api',
  nodeURL: 'https://api.app.altius.syd.life/api/v2',

  cognitoUserPoolWebClientId: '13q4t6aprlh1eh43e34kqumsnf',
  cognitoUserPoolId: 'us-east-2_Xip3C8MT3',
  cognitoRegion: 'us-east-2',

  mlAPIKey: 'bd8d46cb-398c-48dc-81cf-709399d80c45',
  stripeKey:
    'pk_live_51SrxLQEv3ZCFtAE3C5Yvf1029O9ulYTQIA9LRG4TaQkZPfhdjft5igDZSsq4gqykW4auPEZuCXi2F0MHoLYsyP2j00npyVxnzX',

  amplitudeAPIKey: '71cf4fbd4873aac53c3797b3c6295c71',
  termsURL: 'https://www.altiusco.com/terms-conditions',

  name: 'altius',
  theme: altiusTheme,
};

const altiusTest: Environment = {
  ...altius,

  defaultCoupon: 'TEST_MEMBERSHIP_SUB',
  stripeKey:
    'pk_test_51SrxLbIHJDva6uqkEDU8aVMTdDSzvscIpbqL8MGLV4FUhmPnMUjQxP5bmo5yRsIZyURIO54d8cbKfmHsmKx9Zq0a00DnREwIWV',
};

const altiusDev: Environment = {
  ...altius,

  autocomplete: true,
  timeout: 200,
  splashTimeout: 1000,
};

export const environments = {
  alpha,
  dev,
  alphaNodeLocalhost,
  alphaRubyLocalhost,
  alphaLocalhost,

  prod,
  prodNodeLocalhost,

  altius,
  altiusDev,
  altiusTest,
};

export const appEnvironment =
  environments[process.env.REACT_APP_ENVIRONMENT as keyof typeof environments];

export const mlClient = axios.create({
  baseURL: appEnvironment.mlURL,
});
export const rubyClient = axios.create({
  baseURL: appEnvironment.rubyURL,
});
export const nodeClient = axios.create({
  baseURL: appEnvironment.nodeURL,
});

export const appAutoCompleteData = {
  name: 'Lucian',
  email: 'test_new_website389@mailinator.com',
  lastName: 'Test',
  password: 'Password_1',
  phoneNumber: '7484219453223',
  coupon: '',
  birthDate: dayjs().subtract(16, 'years'),
  gender: 'F',

  address: {
    addressLine1: 'Address Line 1',
    addressLine2: 'Address Line 2',
    city: 'London',
    postCode: 'SW2',
  },

  card: {
    cardNumber: '4242424242424242',
    cardExpiry: '12/25',
    cvc: '200',
    cardName: 'Lucian',
  },
};

export const ASSETS_URL = 'https://d3b3zp0qkrdpk.cloudfront.net';
